/**
 * CampaignScene — single-player game vs an AI opponent.
 */

import Phaser from 'phaser'
import { GameLogic, getPiecePositions, PieceState } from '@shared/game/GameLogic'
import { CampaignAI } from '@shared/game/CampaignAI'
import {
  BOARD_COLS, BOARD_ROWS,
  BLOCK_COLOR, BLOCK_DARK, CELL_EMPTY,
  SPEEDS, DAS_DELAY, DAS_REPEAT,
} from '@shared/constants'
// campaign data imported by CampaignMapScene — this scene receives params directly
import { SoundManager } from '../sound/SoundManager'
import { TouchController, isTouchDevice } from '../input/TouchController'
import { loadSettings } from '../data/settings'

// ─── Layout ──────────────────────────────────────────────────────────────────

const P_BS = 44   // player block size
const P_BX = 56   // player board left
const P_BY = 72   // player board top

const A_BS = 44   // AI block size
const A_BX = 650  // AI board left
const A_BY = 72   // AI board top

const MID_X  = P_BX + BOARD_COLS * P_BS + 28   // middle panel left
const MID_CX = P_BX + BOARD_COLS * P_BS + (A_BX - P_BX - BOARD_COLS * P_BS) / 2  // panel center

// ─── CampaignScene ───────────────────────────────────────────────────────────

export class CampaignScene extends Phaser.Scene {
  private levelIndex!:   number
  private stageIndex!:   number
  private aiDifficulty!: number
  private speedKey!:     string
  private stageName!:    string

  private playerLogic!: GameLogic
  private aiLogic!:     GameLogic
  private ai!:          CampaignAI
  private gameEnded  = false
  private playerWon  = false
  private playerScore = 0

  private sfx = new SoundManager()

  // Graphics — created AFTER background (z-order!)
  private playerGfx!: Phaser.GameObjects.Graphics
  private aiGfx!:     Phaser.GameObjects.Graphics

  // HUD text objects
  private txtPlayerScore!: Phaser.GameObjects.Text
  private txtAiScore!:     Phaser.GameObjects.Text
  private txtChain!:       Phaser.GameObjects.Text
  private txtIncoming!:    Phaser.GameObjects.Text

  // Input
  private keyLeft!:  Phaser.Input.Keyboard.Key
  private keyRight!: Phaser.Input.Keyboard.Key
  private keyDown!:  Phaser.Input.Keyboard.Key

  private dasTimer  = 0
  private dasRepeat = 0
  private dasActive = false

  private touch: TouchController | null = null
  private lastSpawnCount = 0

  // Sound state
  private prevPlayerPhase = ''
  private savedFlashCount = 0
  private gameOverSoundPlayed = false

  constructor() { super({ key: 'CampaignScene' }) }

  preload(): void {
    const wp = String(this.levelIndex + 1).padStart(2, '0')
    const sp = String(this.stageIndex + 1).padStart(2, '0')
    const key = `villain_${wp}_${sp}`
    if (!this.textures.exists(key)) {
      this.load.image(key, `assets/villain_${wp}_${sp}.png`)
    }
  }

  init(data: { levelIndex: number; stageIndex: number; aiDifficulty: number; speedKey: string; stageName: string }): void {
    this.levelIndex   = data.levelIndex   ?? 0
    this.stageIndex   = data.stageIndex   ?? 0
    this.aiDifficulty = data.aiDifficulty ?? 1
    this.speedKey     = data.speedKey     ?? 'slow'
    this.stageName    = data.stageName    ?? ''
    this.gameEnded    = false
    this.playerWon    = false
    this.playerScore  = 0
    this.gameOverSoundPlayed = false
    this.prevPlayerPhase     = ''
    this.savedFlashCount     = 0
    this.dasTimer  = 0
    this.dasRepeat = 0
    this.dasActive = false
  }

  create(): void {
    const W     = this.scale.width
    const H     = this.scale.height
    const speed = (SPEEDS as Record<string, number>)[this.speedKey] ?? SPEEDS.normal

    // ── BACKGROUND FIRST (must be before Graphics layers) ────────────────────
    this.add.rectangle(W / 2, H / 2, W, H, 0x0a0a1a)

    // ── Game logic ───────────────────────────────────────────────────────────
    this.playerLogic = new GameLogic(Date.now() | 0, speed)
    this.aiLogic     = new GameLogic((Date.now() ^ 0xcafe1234) | 0, speed)
    this.ai          = new CampaignAI(this.aiDifficulty)

    // ── Graphics layers (added AFTER background) ──────────────────────────────
    this.playerGfx = this.add.graphics()
    this.aiGfx     = this.add.graphics()

    // ── Static HUD elements ───────────────────────────────────────────────────

    // Header bar
    const headerGfx = this.add.graphics()
    headerGfx.fillStyle(0x0d0d20)
    headerGfx.fillRect(0, 0, W, 60)
    headerGfx.lineStyle(1, 0x222244)
    headerGfx.lineBetween(0, 60, W, 60)

    // Stage label
    this.add.text(W / 2, 18, `World ${this.levelIndex + 1}  ·  Stage ${this.stageIndex + 1}  ·  ${this.stageName}  ·  AI ${this.aiDifficulty}  ·  ${this.speedKey.toUpperCase()}`, {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
    }).setOrigin(0.5)

    // ESC / quit button
    const quitBtn = this.add.text(W - 16, 18, isTouchDevice() ? '✕ QUIT' : 'ESC = quit', {
      fontFamily: 'monospace', fontSize: isTouchDevice() ? '16px' : '11px', color: '#333355',
      backgroundColor: isTouchDevice() ? '#1a0a0a' : undefined,
      padding: isTouchDevice() ? { x: 14, y: 8 } : undefined,
    }).setOrigin(1, 0.5).setInteractive()
    quitBtn.on('pointerdown', () => this.returnToMap(false, 0))

    // Player label
    this.add.text(P_BX + (BOARD_COLS * P_BS) / 2, P_BY - 18, 'YOU', {
      fontFamily: 'monospace', fontSize: '13px', color: '#4CC9F0',
    }).setOrigin(0.5)

    // AI label
    this.add.text(A_BX + (BOARD_COLS * A_BS) / 2, A_BY - 18, `AI  LV.${this.aiDifficulty}`, {
      fontFamily: 'monospace', fontSize: '13px', color: '#FF6B6B',
    }).setOrigin(0.5)

    // Score texts
    this.txtPlayerScore = this.add.text(MID_CX, 90, 'SCORE\n0', {
      fontFamily: 'monospace', fontSize: '16px', color: '#e0e0ff', align: 'center',
    }).setOrigin(0.5)

    this.txtAiScore = this.add.text(MID_CX, 150, 'AI\n0', {
      fontFamily: 'monospace', fontSize: '13px', color: '#666688', align: 'center',
    }).setOrigin(0.5)

    // Villain portrait
    const wp = String(this.levelIndex + 1).padStart(2, '0')
    const sp = String(this.stageIndex + 1).padStart(2, '0')
    const vKey = `villain_${wp}_${sp}`
    if (this.textures.exists(vKey)) {
      const portrait = this.add.image(MID_CX, 290, vKey)
      portrait.setDisplaySize(150, 150)
      const mask = this.make.graphics({ x: MID_CX - 75, y: 215 })
      mask.fillStyle(0xffffff)
      mask.fillCircle(75, 75, 75)
      portrait.setMask(mask.createGeometryMask())
      const ring = this.add.graphics()
      ring.lineStyle(3, 0x444466, 0.9)
      ring.strokeCircle(MID_CX, 290, 76)
    }

    // Incoming garbage warning
    this.txtIncoming = this.add.text(MID_CX, 210, '', {
      fontFamily: 'monospace', fontSize: '13px', color: '#FF6B6B', align: 'center',
    }).setOrigin(0.5).setVisible(false)

    // Chain anim (centered on player board)
    this.txtChain = this.add.text(
      P_BX + (BOARD_COLS * P_BS) / 2,
      P_BY + (BOARD_ROWS * P_BS) / 2,
      '', {
        fontFamily: 'monospace', fontSize: '40px',
        color: '#FFE566', stroke: '#000', strokeThickness: 5, align: 'center',
      }
    ).setOrigin(0.5).setDepth(4).setVisible(false)

    // Controls hint
    const touchActive = loadSettings().touchControls === 'on' || (loadSettings().touchControls === 'auto' && isTouchDevice())
    this.add.text(MID_CX, 490,
      touchActive
        ? 'Pan: move\n2-finger: rotate\nSwipe ↓: drop\nTap: hard drop'
        : 'Arrows: move\nUp / X: rotate CW\nZ: rotate CCW\nSPACE: hard drop\nDown: soft drop',
      { fontFamily: 'monospace', fontSize: touchActive ? '12px' : '11px', color: '#2a2a4a', align: 'center', lineSpacing: 4 }
    ).setOrigin(0.5)

    // ── Keyboard ─────────────────────────────────────────────────────────────
    this.keyLeft  = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT)
    this.keyRight = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT)
    this.keyDown  = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN)

    this.input.keyboard!.on('keydown', () => this.sfx.unlock())

    this.input.keyboard!.on('keydown-UP',    () => { if (!this.gameEnded) this.playerLogic.rotateCW() })
    this.input.keyboard!.on('keydown-X',     () => { if (!this.gameEnded) this.playerLogic.rotateCW() })
    this.input.keyboard!.on('keydown-Z',     () => { if (!this.gameEnded) this.playerLogic.rotateCCW() })
    this.input.keyboard!.on('keydown-SPACE', () => { if (!this.gameEnded) this.playerLogic.hardDrop() })

    this.input.keyboard!.on('keydown-DOWN', () => { if (!this.gameEnded) this.playerLogic.softDropStart() })
    this.input.keyboard!.on('keyup-DOWN',   () => this.playerLogic.softDropEnd())
    this.input.keyboard!.on('keydown-S',    () => { if (!this.gameEnded) this.playerLogic.softDropStart() })
    this.input.keyboard!.on('keyup-S',      () => this.playerLogic.softDropEnd())

    this.input.keyboard!.on('keydown-LEFT', () => {
      if (!this.gameEnded) { this.playerLogic.moveLeft(); this.dasTimer = 0; this.dasActive = false }
    })
    this.input.keyboard!.on('keydown-RIGHT', () => {
      if (!this.gameEnded) { this.playerLogic.moveRight(); this.dasTimer = 0; this.dasActive = false }
    })
    this.input.keyboard!.on('keyup-LEFT',  () => { this.dasTimer = 0; this.dasActive = false })
    this.input.keyboard!.on('keyup-RIGHT', () => { this.dasTimer = 0; this.dasActive = false })

    this.input.keyboard!.on('keydown-ESC', () => this.returnToMap(false, 0))

    // ── Touch controls ─────────────────────────────────────────────────────
    const ts = loadSettings().touchControls
    const useTouch = ts === 'on' || (ts === 'auto' && isTouchDevice())
    if (useTouch) {
      this.touch = new TouchController(this, {
        moveLeft:      () => { if (!this.gameEnded) this.playerLogic.moveLeft() },
        moveRight:     () => { if (!this.gameEnded) this.playerLogic.moveRight() },
        rotateCW:      () => { if (!this.gameEnded) this.playerLogic.rotateCW() },
        rotateCCW:     () => { if (!this.gameEnded) this.playerLogic.rotateCCW() },
        hardDrop:      () => { if (!this.gameEnded) this.playerLogic.hardDrop() },
        softDropStart: () => { if (!this.gameEnded) this.playerLogic.softDropStart() },
        softDropEnd:   () => this.playerLogic.softDropEnd(),
      })
    }

    this.events.on('shutdown', () => {
      this.input.keyboard!.removeAllListeners()
      this.touch?.destroy()
    })
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000

    // ── DAS ──────────────────────────────────────────────────────────────────
    if (!this.gameEnded && (this.keyLeft.isDown || this.keyRight.isDown)) {
      this.dasTimer += dt
      if (!this.dasActive && this.dasTimer >= DAS_DELAY) { this.dasActive = true; this.dasRepeat = 0 }
      if (this.dasActive) {
        this.dasRepeat += dt
        if (this.dasRepeat >= DAS_REPEAT) {
          this.dasRepeat = 0
          if (this.keyLeft.isDown)  this.playerLogic.moveLeft()
          if (this.keyRight.isDown) this.playerLogic.moveRight()
        }
      }
    }

    if (!this.gameEnded) {
      // ── Update logic ─────────────────────────────────────────────────────
      this.playerLogic.update(dt)

      // Reset touch gesture when a new piece spawns
      if (this.touch && this.playerLogic.spawnCount !== this.lastSpawnCount) {
        this.lastSpawnCount = this.playerLogic.spawnCount
        this.touch.resetGesture()
      }

      this.ai.update(dt, this.aiLogic)
      this.aiLogic.update(dt)

      // ── Route garbage ─────────────────────────────────────────────────────
      if (this.playerLogic.outgoingGarbage > 0) {
        this.aiLogic.receiveGarbage(this.playerLogic.outgoingGarbage)
        this.playerLogic.outgoingGarbage = 0
      }
      if (this.aiLogic.outgoingGarbage > 0) {
        this.playerLogic.receiveGarbage(this.aiLogic.outgoingGarbage)
        this.aiLogic.outgoingGarbage = 0
      }

      // ── Win / lose detection ──────────────────────────────────────────────
      if (this.aiLogic.phase.kind === 'game_over')     this.endGame(true)
      else if (this.playerLogic.phase.kind === 'game_over') this.endGame(false)
    }

    // ── Sound events ─────────────────────────────────────────────────────────
    this.updateSounds()

    // ── Render ───────────────────────────────────────────────────────────────
    this.renderBoard(this.playerGfx, this.playerLogic, P_BX, P_BY, P_BS, _time, true)
    this.renderBoard(this.aiGfx,     this.aiLogic,     A_BX, A_BY, A_BS, _time, false)
    this.updateHUD()
  }

  // ─── End game ─────────────────────────────────────────────────────────────

  private endGame(won: boolean): void {
    if (this.gameEnded) return
    this.gameEnded   = true
    this.playerWon   = won
    this.playerScore = this.playerLogic.score

    if (won) this.sfx.playWin()
    else     this.sfx.playLose()

    // Overlay
    const W = this.scale.width
    const H = this.scale.height

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55).setDepth(5)
    this.add.text(W / 2, H / 2 - 50, won ? 'YOU WIN!' : 'GAME OVER', {
      fontFamily: 'monospace', fontSize: '52px',
      color: won ? '#4ECB71' : '#FF6B6B',
      stroke: '#000', strokeThickness: 6,
    }).setOrigin(0.5).setDepth(6)

    const scoreLabel = this.add.text(W / 2, H / 2 + 20, `Score: ${this.playerScore.toLocaleString()}`, {
      fontFamily: 'monospace', fontSize: '22px', color: '#e0e0ff',
    }).setOrigin(0.5).setDepth(6)

    const mob = isTouchDevice()
    const cont = this.add.text(W / 2, H / 2 + 80, mob ? 'TAP TO CONTINUE' : 'SPACE  or  click  to continue', {
      fontFamily: 'monospace', fontSize: mob ? '22px' : '16px', color: '#aaaacc',
      backgroundColor: mob ? '#1a1a2e' : undefined,
      padding: mob ? { x: 30, y: 14 } : undefined,
    }).setOrigin(0.5).setDepth(6)

    this.tweens.add({ targets: cont, alpha: 0.2, yoyo: true, repeat: -1, duration: 700 })

    const proceed = () => this.returnToMap(won, this.playerScore)
    this.input.keyboard!.once('keydown-SPACE', proceed)
    this.input.once('pointerdown', proceed)
  }

  private returnToMap(won: boolean, score: number): void {
    this.scene.start('CampaignMapScene', {
      result: { level: this.levelIndex, stage: this.stageIndex, won, score },
    })
  }

  // ─── Sound events ─────────────────────────────────────────────────────────

  private updateSounds(): void {
    const phase = this.playerLogic.phase.kind

    if (phase === 'chain_flash') {
      this.savedFlashCount = Math.max(this.savedFlashCount, this.playerLogic.flashingCells.size)
    }
    if (this.prevPlayerPhase === 'chain_flash' && phase !== 'chain_flash') {
      this.sfx.playPop(Math.max(1, Math.ceil(this.savedFlashCount / 4)))
      this.savedFlashCount = 0
    }
    if (this.playerLogic.chainJustEnded) {
      this.sfx.playChain(this.playerLogic.lastChainCount)
      this.playerLogic.chainJustEnded = false
    }
    if (this.prevPlayerPhase !== 'game_over' && phase === 'game_over' && !this.gameOverSoundPlayed) {
      this.gameOverSoundPlayed = true
      this.sfx.playLose()
    }
    this.prevPlayerPhase = phase
  }

  // ─── HUD ──────────────────────────────────────────────────────────────────

  private updateHUD(): void {
    this.txtPlayerScore.setText(`SCORE\n${this.playerLogic.score.toLocaleString()}`)
    this.txtAiScore.setText(`AI\n${this.aiLogic.score.toLocaleString()}`)

    const inc = this.playerLogic.incomingGarbage
    if (inc > 0) {
      this.txtIncoming.setText(`INCOMING\n${inc} blocks`).setVisible(true)
    } else {
      this.txtIncoming.setVisible(false)
    }

    if (this.playerLogic.chainDisplayTimer > 0) {
      const n = this.playerLogic.lastChainCount
      this.txtChain
        .setText(`x${n} CHAIN!`)
        .setAlpha(Math.min(1, this.playerLogic.chainDisplayTimer / 0.5))
        .setVisible(true)
    } else if (this.playerLogic.currentChainCount >= 2) {
      this.txtChain.setText(`x${this.playerLogic.currentChainCount}`).setAlpha(1).setVisible(true)
    } else {
      this.txtChain.setVisible(false)
    }
  }

  // ─── Board rendering ──────────────────────────────────────────────────────

  private renderBoard(
    gfx: Phaser.GameObjects.Graphics,
    logic: GameLogic,
    bx: number, by: number, bs: number,
    time: number,
    isPlayer: boolean,
  ): void {
    gfx.clear()

    const bw = BOARD_COLS * bs
    const bh = BOARD_ROWS * bs

    // Background
    gfx.fillStyle(0x0d0d20)
    gfx.fillRect(bx, by, bw, bh)

    // Grid lines
    gfx.lineStyle(1, 0x181830, 0.6)
    for (let c = 1; c < BOARD_COLS; c++)
      gfx.lineBetween(bx + c * bs, by, bx + c * bs, by + bh)
    for (let r = 1; r < BOARD_ROWS; r++)
      gfx.lineBetween(bx, by + r * bs, bx + bw, by + r * bs)

    // Cells
    const flashing = logic.flashingCells
    const inFlash  = logic.phase.kind === 'chain_flash'
    const flashOn  = inFlash && (Math.floor(time / 80) % 2 === 0)

    for (let row = 0; row < BOARD_ROWS; row++) {
      for (let col = 0; col < BOARD_COLS; col++) {
        const val = logic.board.get(col, row)
        if (val === CELL_EMPTY) continue

        const key   = `${col},${row}`
        const glow  = inFlash && flashing.has(key)
        const alpha = glow && !flashOn ? 0.22 : 1.0
        const color = glow && flashOn  ? 0xffffff : BLOCK_COLOR[val]
        const dark  = glow && flashOn  ? 0xcccccc : BLOCK_DARK[val]

        this.drawBlock(gfx, bx + col * bs, by + row * bs, bs, color, dark, alpha)
      }
    }

    // Ghost piece (player only, falling phase)
    if (isPlayer && logic.phase.kind === 'falling') {
      const gr = logic.ghostRow
      if (gr !== logic.current.row) {
        const ghost: PieceState = { ...logic.current, row: gr }
        const [[c1, r1], [c2, r2]] = getPiecePositions(ghost)
        this.drawBlockGhost(gfx, bx + c1 * bs, by + r1 * bs, bs, BLOCK_COLOR[ghost.color1])
        this.drawBlockGhost(gfx, bx + c2 * bs, by + r2 * bs, bs, BLOCK_COLOR[ghost.color2])
      }
    }

    // Falling piece
    if (logic.phase.kind === 'falling') {
      const [[c1, r1], [c2, r2]] = getPiecePositions(logic.current)
      const cur = logic.current
      this.drawBlock(gfx, bx + c1 * bs, by + r1 * bs, bs, BLOCK_COLOR[cur.color1], BLOCK_DARK[cur.color1])
      this.drawBlock(gfx, bx + c2 * bs, by + r2 * bs, bs, BLOCK_COLOR[cur.color2], BLOCK_DARK[cur.color2])
    }

    // Border (player=blue, AI=red)
    gfx.lineStyle(2, isPlayer ? 0x2255cc : 0xcc2233)
    gfx.strokeRect(bx, by, bw, bh)

    // Next piece preview
    this.drawNextPreview(gfx, logic, bx + bw + 6, by, bs)
  }

  // ─── Next piece preview ───────────────────────────────────────────────────

  private drawNextPreview(
    gfx: Phaser.GameObjects.Graphics,
    logic: GameLogic,
    nx: number, ny: number, bs: number,
  ): void {
    const s  = Math.floor(bs * 0.65)
    const pw = s * 2 + 8
    const ph = s * 2 + 8

    gfx.fillStyle(0x0d0d20)
    gfx.fillRoundedRect(nx, ny, pw, ph, 4)
    gfx.lineStyle(1, 0x222244)
    gfx.strokeRoundedRect(nx, ny, pw, ph, 4)

    const cx = Math.floor(nx + pw / 2 - s / 2)
    const np = logic.nextPiece
    this.drawBlock(gfx, cx, ny + 4,      s, BLOCK_COLOR[np.color2], BLOCK_DARK[np.color2])
    this.drawBlock(gfx, cx, ny + 4 + s,  s, BLOCK_COLOR[np.color1], BLOCK_DARK[np.color1])
  }

  // ─── Block helpers ────────────────────────────────────────────────────────

  private drawBlock(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, bs: number,
    color: number, dark: number, alpha = 1,
  ): void {
    const p = 2
    gfx.fillStyle(color, alpha)
    gfx.fillRoundedRect(x + p, y + p, bs - p * 2, bs - p * 2, 4)
    gfx.fillStyle(0xffffff, 0.20 * alpha)
    gfx.fillRect(x + p + 2, y + p + 2, bs - p * 2 - 4, Math.floor((bs - p * 2) * 0.38))
    gfx.fillStyle(dark, 0.35 * alpha)
    gfx.fillRect(x + p + 2, y + bs - p - 5, bs - p * 2 - 4, 4)
  }

  private drawBlockGhost(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, bs: number, color: number,
  ): void {
    gfx.fillStyle(color, 0.16)
    gfx.fillRoundedRect(x + 2, y + 2, bs - 4, bs - 4, 4)
  }
}
